import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "./quill-mention/quill.mention.css";
import Quill from "quill";
import "./editor.css";
import { ImageResize } from "quill-image-resize-module-ts";
import { useEffect, useRef } from "react";
import LongTextViewer from "./LongTextViewer";
import { MentionBlot, Mention } from "./quill-mention";
import { fromJS } from "immutable";

Quill.register("modules/imageResize", ImageResize);

const Block: any = Quill.import("blots/block");
Block.tagName = "div";
Quill.register(Block, true);
Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

class StyledMentionBlot extends MentionBlot {
  static render(data) {
    const element = document.createElement("span");
    element.className = "mention";
    element.style.color = "#2563eb"; // text-blue-600
    element.style.fontWeight = "500";
    element.style.fontSize = "14px";
    element.style.lineHeight = "20px";
    if (data.type == "mention") {
      element.innerText = `@${data.value}`;
    } else if (data.type == "smartField") {
      element.innerText = data.id || "";
    } else {
      element.innerText = `${data.id}`;
    }
    return element;
  }
}
StyledMentionBlot.blotName = "mention2";

Quill.register(StyledMentionBlot);

const Editor = ({
  placeholder = "Start by typing @ for mentions",
  editorContent = { htmlContent: "", contents: {} },
  showToolbar = true,
  viewMode = false,
  readOnly = false,
  editorHeight = "auto",
  minRows = undefined,
  lineHeightPx = 22,
  attributes = fromJS([]),
  members = fromJS([]),
  onChange,
  editorInputRef,
  hideBorder = false,
  className = "",
  toolbarContainer = undefined,
  onOpenVariablePicker = undefined,
}: any) => {
  const editorContainerRef = useRef(null);

  const allPeople = members
    .map((member: any) => {
      const displayName =
        member.get("displayName") ||
        member.get("name") ||
        member.get("email") ||
        member.get("accountId") ||
        "Unknown";
      const idVal = member.get("accountId") || member.get("id") || displayName;
      return {
        id: String(idVal),
        value: String(displayName),
        type: "mention",
      };
    })
    .toJS();

  const suggestPeople = (searchTerm: string) => {
    const term = String(searchTerm || "").toLowerCase();
    if (term.length === 0) {
      return allPeople;
    }
    return allPeople.filter((person: any) =>
      String(person.value || "")
        .toLowerCase()
        .includes(term)
    );
  };

  const suggestSmartValues = async (searchTerm: string) => {
    const smartValues = attributes
      .map((item: any) => {
        return {
          id: `{{${item.get("value")}}}`,
          value: item.get("value"),
          type: "smartField",
        };
      })
      .toJS();
    if (searchTerm.length === 0) {
      return smartValues;
    }
    return smartValues.filter((item: any) => item.value.includes(searchTerm));
  };

  const handleOnChange = (content: any) => {
    const data = {
      ...content,
      mentionedMembers: getMentionedMembers(content.contents),
    };
    if (onChange) onChange(data);
  };

  const getMentionedMembers = (contents = { ops: [] }) => {
    const mentionOps = contents.ops.filter((op: any) => op.insert.mention2);
    const mentionedItems = mentionOps.map((op: any) => {
      return {
        id: op.insert.mention2.id,
        value: op.insert.mention2.value,
        type: op.insert.mention2.type,
      };
    });
    return mentionedItems.filter((item) => item.type == "mention");
  };

  const getEditorOptions = () => {
    if (viewMode) {
      return {
        readOnly: true,
        modules: {
          toolbar: false,
        },
      };
    }
    let toolbar: any = false;
    if (toolbarContainer?.current) {
      toolbar = { container: toolbarContainer.current };
    } else if (showToolbar) {
      toolbar = [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }, "link"],
      ];
    }
    return {
      placeholder,
      theme: "snow",
      readOnly,
      modules: {
        toolbar,
        imageResize: {
          modules: ["Resize", "DisplaySize"],
        },
        mention: {
          // allow empty search term so menu opens right after typing '@'
          allowedChars: () => /.*/,
          minChars: 0,
          mentionDenotationChars: ["@"],
          renderItem: (data) => {
            if (data.type == "mention") {
              return `${data.value}`;
            }
            return `${data.id}`;
          },
          positioningStrategy: "fixed",
          showDenotationChar: false,
          source: async (searchTerm: any, renderList: any, mentionChar: string) => {
            if (mentionChar == "@") {
              const matchedPeople = suggestPeople(searchTerm);
              renderList(matchedPeople, searchTerm);
            }
            if (mentionChar == "<") {
              const matchedField = await suggestSmartValues(searchTerm);
              renderList(matchedField, searchTerm);
            }
          },
          dataAttributes: ["id", "value", "type", "denotationChar"],
          blotName: "mention2",
        },
      },
    };
  };

  useEffect(() => {
    const container: any = editorContainerRef.current;
    if (container) {
      // teardown any previous instance by clearing container
      container.innerHTML = "";
      const quill = new Quill(container, getEditorOptions());
      const delta = quill.clipboard.convert({
        html: editorContent.htmlContent,
      });
      quill.setContents(delta);
      let lastCharWasBrace = false;
      quill.on("text-change", (delta: any, _oldDelta: any, source: string) => {
        // Detect '{{' typed by user → delete both '{' and open variable picker
        if (source === "user" || source === "api") {
          const images = quill.root.querySelectorAll("img");
          images.forEach((img) => {
            // apply only once
            if (!img.dataset.initialized) {
              img.dataset.initialized = "true";
              img.width = 300; // default width
              img.style.height = "auto";
            }
          });
        }
        
        if (source === "user" && onOpenVariablePicker) {
          const ops = delta.ops || [];
          const insertedChar = ops.find(
            (op: any) => typeof op.insert === "string" && op.insert.length === 1
          )?.insert;

          if (insertedChar === "{") {
            if (lastCharWasBrace) {
              // Second '{' → delete both '{{' and open picker
              let insertIndex = 0;
              for (const op of ops) {
                if (typeof op.retain === "number") insertIndex += op.retain;
                else break;
              }
              quill.deleteText(insertIndex - 1, 2);
              lastCharWasBrace = false;
              onOpenVariablePicker();
              return;
            } else {
              lastCharWasBrace = true;
              // fall through to normal onChange for the first '{'
            }
          } else {
            lastCharWasBrace = false;
          }
        }
        let htmlContent = quill.getSemanticHTML();
        let textContent = quill.getText() || "";
        if (textContent == "\n") {
          textContent = "";
          htmlContent = "";
        }
        const contents = quill.getContents();
        handleOnChange({ htmlContent, textContent, contents });
      });

      if (hideBorder) {
        const removeBorder = () => {
          // Target .ql-container.ql-snow specifically (as shown in DevTools)
          const quillContainer = container.querySelector(".ql-container.ql-snow");
          const quillEditor = container.querySelector(".ql-editor");
          if (quillContainer) {
            (quillContainer as HTMLElement).style.border = "none";
            (quillContainer as HTMLElement).style.borderWidth = "0";
            (quillContainer as HTMLElement).style.borderStyle = "none";
          }
          if (quillEditor) {
            (quillEditor as HTMLElement).style.border = "none";
            (quillEditor as HTMLElement).style.borderWidth = "0";
            (quillEditor as HTMLElement).style.borderStyle = "none";
          }
        };

        // Apply immediately and also after a short delay to ensure it sticks
        removeBorder();
        setTimeout(removeBorder, 0);
        setTimeout(removeBorder, 10);
      }

      if (editorInputRef) editorInputRef.current = quill;
    }
  }, [
    editorContainerRef,
    members,
    attributes,
    showToolbar,
    readOnly,
    hideBorder,
    toolbarContainer,
  ]);

  if (viewMode) {
    if (editorContent.htmlContent == "") {
      return null;
    }
    return <LongTextViewer html={editorContent.htmlContent} />;
  }

  return (
    <div
      ref={editorContainerRef}
      style={{
        height: editorHeight,
        minHeight:
          typeof minRows === "number" && minRows > 0 ? `${minRows * lineHeightPx}px` : undefined,
      }}
      className={`w-full ${hideBorder ? "editor-no-border" : ""} ${className}`}
    />
  );
};
export default Editor;
